import { AFFILIATE_LINKS } from "./affiliateLinks";

// Pricing figures are list prices researched July 2026 for ~1,000-2,500
// contacts. Vendors change pricing frequently; always confirm on the vendor site.
export const PLATFORMS = [
  {
    slug: "getresponse",
    name: "GetResponse",
    monogram: "GR",
    hue: "#3ABFF8",
    rating: 4.8,
    rank: 1,
    badge: "Best Overall",
    bestFor: "All-in-one marketing on a small-business budget",
    startingPrice: "$19",
    priceNote: "per month · 1,000 contacts",
    annualNote: "$15.58/mo billed annually (save 18%)",
    freePlan: "Free plan · 500 contacts",
    trial: "30-day free trial",
    affiliateUrl: AFFILIATE_LINKS.getresponse,
    tagline:
      "The rare platform that bundles email, automation, landing pages, webinars, and AI tools into one subscription a small business can actually afford.",
    quickVerdict:
      "GetResponse is our top pick for 2026. It is the only platform in this price class that combines genuinely powerful marketing automation with landing pages, conversion funnels, webinars, and an AI email generator. The interface occasionally feels busy, and the cheapest tier limits automation, but for the money, nothing else covers this much ground.",
    scores: [
      { label: "Ease of Use", value: 8.6 },
      { label: "Features", value: 9.6 },
      { label: "Deliverability", value: 9.2 },
      { label: "Support", value: 8.8 },
      { label: "Value for Money", value: 9.5 },
    ],
    pros: [
      "Best feature-per-dollar ratio of any platform we tested",
      "Webinars and conversion funnels built in — no extra tools needed",
      "Visual automation builder with 40+ pre-built workflow templates",
      "AI email and subject-line generator included on all paid plans",
      "24/7 live chat support in 8 languages, even on entry plans",
      "Generous free plan with a website builder included",
      "Consistently top-tier deliverability in independent tests",
    ],
    cons: [
      "Full marketing automation requires the Marketer tier (~$59/mo)",
      "Interface can feel dense for first-time users",
      "Template designs lag behind MailerLite's modern look",
      "List-size pricing climbs quickly past 10,000 contacts",
    ],
    pricingTiers: [
      {
        name: "Free",
        monthly: "$0",
        annual: "$0",
        contacts: "500 contacts",
        popular: false,
        features: ["2,500 emails/mo", "1 landing page", "Website builder", "Signup forms", "Basic segmentation"],
      },
      {
        name: "Starter",
        monthly: "$19",
        annual: "$15.58",
        contacts: "1,000 contacts",
        popular: false,
        features: ["Unlimited emails", "AI email generator", "Autoresponders", "Landing pages", "Basic segmentation"],
      },
      {
        name: "Marketer",
        monthly: "$59",
        annual: "$48.38",
        contacts: "1,000 contacts",
        popular: true,
        features: ["Everything in Starter", "Full marketing automation", "Abandoned cart recovery", "Webinars (100 attendees)", "Contact scoring & tagging"],
      },
      {
        name: "Creator",
        monthly: "$69",
        annual: "$56.58",
        contacts: "1,000 contacts",
        popular: false,
        features: ["Everything in Marketer", "Online courses", "Premium newsletters", "Quizzes", "Creator monetization tools"],
      },
      {
        name: "MAX",
        monthly: "Custom",
        annual: "Custom",
        contacts: "100k+ contacts",
        popular: false,
        features: ["Dedicated IP address", "SMS marketing", "Transactional emails", "SSO & priority support", "Deliverability consulting"],
      },
    ],
    matrix: {
      freePlan: "500 contacts",
      automation: "Advanced (visual)",
      landingPages: true,
      webinars: true,
      crm: "Basic",
      abTesting: "5 variants",
      templates: "150+",
      support: "24/7 live chat",
    },
    integrations: ["Shopify", "WooCommerce", "WordPress", "Zapier", "Stripe", "PayPal", "Salesforce", "Magento", "Facebook Ads", "Google Analytics"],
    idealFor: [
      "Small businesses that want email + landing pages + webinars in one bill",
      "Ecommerce stores needing abandoned cart and product recommendation automation",
      "Course creators and coaches selling digital products",
      "Marketers who want AI-assisted campaign creation",
    ],
  },
  {
    slug: "aweber",
    name: "AWeber",
    monogram: "AW",
    hue: "#F8B63A",
    rating: 4.5,
    rank: 2,
    badge: "Best Support",
    bestFor: "Beginners who want hand-holding and legendary support",
    startingPrice: "$15",
    priceNote: "per month · 500 subscribers",
    annualNote: "$12.50/mo billed annually",
    freePlan: "Free plan · 500 subscribers",
    trial: "Free plan available",
    affiliateUrl: AFFILIATE_LINKS.aweber,
    tagline:
      "A 27-year industry veteran with the best customer support in the business and deliverability that consistently punches above its price.",
    quickVerdict:
      "AWeber is the steady, trustworthy veteran of email marketing. It lacks the flashy automation depth of GetResponse or the modern polish of MailerLite, but it compensates with outstanding US-based phone support, excellent deliverability, and a Canva integration that makes design painless. Ideal for beginners who value help over horsepower.",
    scores: [
      { label: "Ease of Use", value: 9.0 },
      { label: "Features", value: 7.8 },
      { label: "Deliverability", value: 9.4 },
      { label: "Support", value: 9.8 },
      { label: "Value for Money", value: 8.2 },
    ],
    pros: [
      "Best-in-class support: 24/7 live chat plus real phone support",
      "Top-tier deliverability backed by 25+ years of sender reputation",
      "Free migration service moves your list and automations for you",
      "Canva integration built directly into the email editor",
      "700+ mobile-responsive templates — the largest library we tested",
      "Free plan includes automation and landing pages",
      "Unlimited email sends on every paid plan",
    ],
    cons: [
      "Automation builder is simpler than GetResponse or Moosend",
      "Charges for unsubscribed contacts still stored in your account",
      "Reporting dashboards feel dated next to newer rivals",
      "Pricing climbs steeply as your list grows past 2,500",
    ],
    pricingTiers: [
      {
        name: "Free",
        monthly: "$0",
        annual: "$0",
        contacts: "500 subscribers",
        popular: false,
        features: ["3,000 emails/mo", "1 landing page", "1 automation", "Basic reporting", "AWeber branding"],
      },
      {
        name: "Lite",
        monthly: "$15",
        annual: "$12.50",
        contacts: "500 subscribers",
        popular: false,
        features: ["Unlimited emails", "3 automations", "Advanced segments", "24/7 support", "Remove branding (add-on)"],
      },
      {
        name: "Plus",
        monthly: "$30",
        annual: "$20",
        contacts: "500 subscribers",
        popular: true,
        features: ["Everything in Lite", "Unlimited automations", "Unlimited landing pages", "Sales tracking", "A/B split testing", "Priority support"],
      },
      {
        name: "Unlimited",
        monthly: "$899",
        annual: "Custom",
        contacts: "Unlimited subscribers",
        popular: false,
        features: ["Fixed price, unlimited list", "Dedicated IP", "Concierge migration", "Personalized onboarding", "Advanced account management"],
      },
    ],
    matrix: {
      freePlan: "500 subscribers",
      automation: "Basic–mid",
      landingPages: true,
      webinars: false,
      crm: false,
      abTesting: "3 variants",
      templates: "700+",
      support: "24/7 chat + phone",
    },
    integrations: ["WordPress", "Shopify", "WooCommerce", "Zapier", "Canva", "PayPal", "Etsy", "Squarespace", "Facebook Lead Ads", "GoToWebinar"],
    idealFor: [
      "First-time email marketers who want real humans on the phone",
      "Bloggers and newsletter writers who prioritize deliverability",
      "Small teams that value template variety over automation depth",
      "Businesses migrating from another platform (free concierge move)",
    ],
  },
  {
    slug: "mailerlite",
    name: "MailerLite",
    monogram: "ML",
    hue: "#4ADE80",
    rating: 4.7,
    rank: 3,
    badge: "Best Value",
    bestFor: "Budget-conscious teams who refuse to compromise on design",
    startingPrice: "$10",
    priceNote: "per month · 500 subscribers",
    annualNote: "$9/mo billed annually",
    freePlan: "Free plan · 1,000 subscribers",
    trial: "30-day premium trial",
    affiliateUrl: AFFILIATE_LINKS.mailerlite,
    tagline:
      "The cleanest, most enjoyable email editor on the market — at roughly half the price of its better-known competitors.",
    quickVerdict:
      "MailerLite is the value champion of 2026. It delivers 90% of what premium platforms offer — automation, segmentation, landing pages, A/B testing — at a fraction of the cost, wrapped in the most intuitive interface we have tested. Trade-offs: a strict approval process, no phone support, and fewer native integrations. For most small businesses, it is the smartest first purchase.",
    scores: [
      { label: "Ease of Use", value: 9.6 },
      { label: "Features", value: 8.4 },
      { label: "Deliverability", value: 8.8 },
      { label: "Support", value: 8.0 },
      { label: "Value for Money", value: 9.8 },
    ],
    pros: [
      "Roughly 40-60% cheaper than comparable platforms at every tier",
      "Most intuitive drag-and-drop editor we tested — genuinely enjoyable",
      "Generous free plan: 1,000 subscribers with automation included",
      "Clean, modern email templates that look great on mobile",
      "Built-in website and landing page builder",
      "Paid newsletter subscriptions (sell access directly)",
      "Transparent pricing with no hidden contact-count traps",
    ],
    cons: [
      "Strict account approval process can delay your first send",
      "No phone support — email and live chat only",
      "Fewer native integrations than GetResponse or AWeber",
      "Advanced features (AI writing, custom HTML editor) need the Advanced plan",
    ],
    pricingTiers: [
      {
        name: "Free",
        monthly: "$0",
        annual: "$0",
        contacts: "1,000 subscribers",
        popular: false,
        features: ["12,000 emails/mo", "Automation builder", "10 landing pages", "Signup forms & popups", "Email support (30 days)"],
      },
      {
        name: "Growing Business",
        monthly: "$10",
        annual: "$9",
        contacts: "500 subscribers",
        popular: true,
        features: ["Unlimited emails", "3 user seats", "Dynamic emails", "Sell digital products", "24/7 email support"],
      },
      {
        name: "Advanced",
        monthly: "$20",
        annual: "$18",
        contacts: "500 subscribers",
        popular: false,
        features: ["Everything in Growing", "AI writing assistant", "Facebook integration", "Custom HTML editor", "15% off Google Workspace", "24/7 live chat"],
      },
      {
        name: "Enterprise",
        monthly: "Custom",
        annual: "Custom",
        contacts: "100k+ subscribers",
        popular: false,
        features: ["Dedicated IP & deliverability help", "Dedicated success manager", "Custom onboarding", "Unlimited user seats", "SLA support"],
      },
    ],
    matrix: {
      freePlan: "1,000 subscribers",
      automation: "Advanced (visual)",
      landingPages: true,
      webinars: false,
      crm: false,
      abTesting: "A/B + multivariate",
      templates: "90+",
      support: "24/7 email & chat",
    },
    integrations: ["Shopify", "WooCommerce", "WordPress", "Zapier", "Stripe", "Squarespace", "Wix", "Facebook Lead Ads", "Typeform", "Make"],
    idealFor: [
      "Solopreneurs and micro-businesses watching every dollar",
      "Newsletter writers who want paid subscriptions built in",
      "Design-sensitive brands that need beautiful emails fast",
      "Nonprofits (30% discount) and early-stage startups",
    ],
  },
  {
    slug: "moosend",
    name: "Moosend",
    monogram: "MO",
    hue: "#8A7CF8",
    rating: 4.4,
    rank: 4,
    badge: "Best Automation Value",
    bestFor: "Data-hungry marketers who want premium automation for less",
    startingPrice: "$9",
    priceNote: "per month · 500 subscribers",
    annualNote: "$7/mo billed annually",
    freePlan: "30-day free trial",
    trial: "30-day free trial",
    affiliateUrl: AFFILIATE_LINKS.moosend,
    tagline:
      "Enterprise-grade automation workflows and granular reporting at a price point that undercuts almost everyone.",
    quickVerdict:
      "Moosend is the dark horse of this list. Its visual automation builder rivals tools costing three times as much, and its reporting goes deeper than most small businesses will ever need. The catch: no forever-free plan, a smaller template library, and a brand name your boss may not recognize. If automation sophistication per dollar is your metric, Moosend wins.",
    scores: [
      { label: "Ease of Use", value: 8.4 },
      { label: "Features", value: 8.8 },
      { label: "Deliverability", value: 8.6 },
      { label: "Support", value: 8.2 },
      { label: "Value for Money", value: 9.4 },
    ],
    pros: [
      "Cheapest full-featured automation in this roundup (from $9/mo)",
      "Visual workflow builder with 30+ pre-built automation recipes",
      "Weather-based and product-recommendation content blocks",
      "Deep real-time analytics: heat maps, device, geography, clients",
      "Unlimited emails and landing pages on the Pro plan",
      "Backed by Sitecore (acquired 2021) — financially stable",
    ],
    cons: [
      "No forever-free plan — only a 30-day trial",
      "Smaller template library (~75) than AWeber or GetResponse",
      "No SMS marketing on the standard plan",
      "Less brand recognition means fewer third-party tutorials",
    ],
    pricingTiers: [
      {
        name: "Free Trial",
        monthly: "$0",
        annual: "$0",
        contacts: "1,000 subscribers · 30 days",
        popular: false,
        features: ["Unlimited emails", "Automation workflows", "1 landing page & form", "Reporting & analytics", "No credit card required"],
      },
      {
        name: "Pro",
        monthly: "$9",
        annual: "$7",
        contacts: "500 subscribers",
        popular: true,
        features: ["Unlimited emails", "Full automation builder", "Landing pages & forms", "SMTP server", "Chat & email support"],
      },
      {
        name: "Moosend+",
        monthly: "Custom",
        annual: "Custom",
        contacts: "Any list size",
        popular: false,
        features: ["Everything in Pro", "Transactional emails", "Custom reporting", "SSO & SAML", "Dedicated IP", "Priority support"],
      },
      {
        name: "Enterprise",
        monthly: "Custom",
        annual: "Custom",
        contacts: "High volume",
        popular: false,
        features: ["Dedicated account manager", "Deliverability strategy", "Custom SLAs", "Team training", "Migration assistance"],
      },
    ],
    matrix: {
      freePlan: "30-day trial only",
      automation: "Advanced (visual)",
      landingPages: true,
      webinars: false,
      crm: "Audience fields",
      abTesting: "A/B testing",
      templates: "75+",
      support: "Chat + email",
    },
    integrations: ["WooCommerce", "Shopify", "WordPress", "Zapier", "Magento", "Salesforce", "Facebook Lead Ads", "Google Contacts", "Stripe", "Make"],
    idealFor: [
      "Ecommerce stores that live inside their automation workflows",
      "Agencies needing powerful reporting at client-friendly prices",
      "Marketers graduating from a basic tool who want more logic",
      "Teams that send high volumes and need unlimited emails",
    ],
  },
  {
    slug: "convertkit",
    name: "ConvertKit (Kit)",
    monogram: "CK",
    hue: "#F87C5A",
    rating: 4.6,
    rank: 5,
    badge: "Best for Creators",
    bestFor: "Newsletter writers, course creators, and digital sellers",
    startingPrice: "$29",
    priceNote: "per month · 1,000 subscribers",
    annualNote: "$25/mo billed annually",
    freePlan: "Free plan · 10,000 subscribers",
    trial: "14-day free trial",
    affiliateUrl: AFFILIATE_LINKS.convertkit,
    tagline:
      "Rebranded as Kit in 2024, it remains the platform built by creators, for creators — with the most generous free plan in the industry.",
    quickVerdict:
      "ConvertKit (now Kit) is unapologetically creator-first. Its tag-based subscriber system, visual automations, paid newsletter support, and Creator Network make it the obvious choice if you sell knowledge. It is expensive at scale and overkill for traditional small-business promotions, but for creators, the 10,000-subscriber free plan is unbeatable.",
    scores: [
      { label: "Ease of Use", value: 9.2 },
      { label: "Features", value: 8.2 },
      { label: "Deliverability", value: 9.0 },
      { label: "Support", value: 8.4 },
      { label: "Value for Money", value: 8.0 },
    ],
    pros: [
      "Industry-leading free plan: 10,000 subscribers, 1 automation",
      "Tag-based (not list-based) system prevents duplicate billing",
      "Sell digital products and paid newsletters with 0% platform fees",
      "Creator Network drives subscriber growth via recommendations",
      "Clean visual automation builder with creator-focused templates",
      "Free migration from any platform on paid plans",
    ],
    cons: [
      "Expensive once you pass 1,000-3,000 subscribers",
      "Email design options are deliberately minimal",
      "Reporting is basic compared to Moosend or GetResponse",
      "Not built for traditional ecommerce promotions",
    ],
    pricingTiers: [
      {
        name: "Newsletter (Free)",
        monthly: "$0",
        annual: "$0",
        contacts: "10,000 subscribers",
        popular: false,
        features: ["1 automation & sequence", "Unlimited landing pages", "Creator Network", "Sell digital products", "1 team member"],
      },
      {
        name: "Creator",
        monthly: "$29",
        annual: "$25",
        contacts: "1,000 subscribers",
        popular: true,
        features: ["Unlimited automations", "Visual automation builder", "Free list migration", "Remove Kit branding", "Live chat support"],
      },
      {
        name: "Creator Pro",
        monthly: "$59",
        annual: "$50",
        contacts: "1,000 subscribers",
        popular: false,
        features: ["Everything in Creator", "Subscriber scoring", "Advanced reporting", "Newsletter referral system", "Facebook custom audiences", "Priority support"],
      },
    ],
    matrix: {
      freePlan: "10,000 subscribers",
      automation: "Visual (creator-focused)",
      landingPages: true,
      webinars: false,
      crm: "Tag-based",
      abTesting: "Subject lines",
      templates: "Minimal by design",
      support: "Chat + email",
    },
    integrations: ["Shopify", "WordPress", "Zapier", "Stripe", "Teachable", "Thinkific", "Circle", "Gumroad", "Squarespace", "Patreon"],
    idealFor: [
      "Newsletter operators and independent writers",
      "Course creators and coaches selling digital products",
      "Podcasters and YouTubers converting audiences to email lists",
      "Creators who want to grow through cross-recommendations",
    ],
  },
];

export const getPlatform = (slug) => PLATFORMS.find((p) => p.slug === slug);

export const MATRIX_ROWS = [
  { key: "startingPrice", label: "Starting price", render: (p) => `${p.startingPrice}/mo` },
  { key: "freePlan", label: "Free plan", render: (p) => p.matrix.freePlan },
  { key: "automation", label: "Automation", render: (p) => p.matrix.automation },
  { key: "landingPages", label: "Landing pages", bool: true },
  { key: "webinars", label: "Webinars", bool: true },
  { key: "abTesting", label: "A/B testing", render: (p) => p.matrix.abTesting },
  { key: "templates", label: "Templates", render: (p) => p.matrix.templates },
  { key: "support", label: "Support", render: (p) => p.matrix.support },
];
